import {
  CalendarMeetingTemplateFormData,
  MeetingLocation
} from '../constants/calendarMeetingTemplateSchema';
import { UseUserCalendarReturnData } from '../hooks/useUserCalendar';
import { convertOwnerToParticipants } from './convertOwnerToParticipants';
import { convertFromParticipantsToParticipants } from './covertFromParticipantsToParticipants';
import { convertAvailabilityToSchema } from './convertAvailabilityToSchema';
import { getPanelTypeLabel } from './getPanelTypeLabel';

export const mapToSummaryMeetingTemplate = (
  formData: CalendarMeetingTemplateFormData,
  userCalendars: UseUserCalendarReturnData
) => {
  // Get owner details
  const ownerData = convertOwnerToParticipants(
    formData.owner ?? '',
    userCalendars
  );
  const ownerName = ownerData[0]?.name;

  // Get participants details
  const participantsData = convertFromParticipantsToParticipants(
    formData.participants,
    userCalendars
  );

  const participantsList = participantsData.map(
    (participant) => participant.name
  );

  // Format availability
  const availabilityDays = convertAvailabilityToSchema([
    { day: 0, availability: formData.mondayAvailability },
    { day: 1, availability: formData.tuesdayAvailability },
    { day: 2, availability: formData.wednesdayAvailability },
    { day: 3, availability: formData.thursdayAvailability },
    { day: 4, availability: formData.fridayAvailability },
    { day: 5, availability: formData.saturdayAvailability },
    { day: 6, availability: formData.sundayAvailability }
  ]);

  const daysMap: Record<string, string> = {
    '0': 'Monday',
    '1': 'Tuesday',
    '2': 'Wednesday',
    '3': 'Thursday',
    '4': 'Friday',
    '5': 'Saturday',
    '6': 'Sunday'
  };

  const formattedAvailability = Object.entries(availabilityDays)
    .map(([day, slots]) =>
      slots?.length
        ? `${daysMap[day]} ${slots
            .map((slot) => `${slot.start} - ${slot.end}`)
            .join(', ')}`
        : ''
    )
    .filter(Boolean);

  return [
    { key: 'title', label: 'Title', value: formData.title },
    { key: 'description', label: 'Description', value: formData.description },
    { key: 'location', label: 'Location', value: formData.location },
    {
      key: 'locationDetails',
      label: 'Location details',
      // The address is kept while video is chosen, but it is not sent.
      value:
        formData.location === MeetingLocation.InPerson
          ? formData.locationDetails
          : undefined
    },
    {
      key: 'durationValue',
      label: 'Duration',
      value: `${formData.durationValue} ${formData.durationUnit}`
    },
    { key: 'owner', label: 'Organiser', value: ownerName },
    { key: 'participants', label: 'Participants', value: participantsList },
    {
      key: 'panelType',
      label: 'Interview panel',
      value: getPanelTypeLabel(formData.schedulingMethod)
    },
    {
      key: 'availability',
      label: 'Availability',
      value: formattedAvailability
    },
    { key: 'timeZone', label: 'Timezone', value: formData.timeZone },
    {
      key: 'sendMeetingReminderValue',
      label: 'Meeting reminder',
      value: `${formData.sendMeetingReminderValue} ${formData.sendMeetingReminderUnit} before`
    },
    {
      key: 'bufferTimeValue',
      label: 'Buffer time',
      value: `${formData.bufferTimeValue} ${formData.bufferTimeUnit}`
    },
    {
      key: 'noticePeriodValue',
      label: 'Notice period',
      value: `${formData.noticePeriodValue} ${formData.noticePeriodUnit}`
    },
    {
      key: 'bookBeforeValue',
      label: 'Book before',
      value: `${formData.bookBeforeValue} days`
    }
  ];
};
